import * as React from 'react';

const ESCAPE_KEY = 'Escape';
const TAB_KEY = 'Tab';
const FOCUSABLE_SELECTOR = [
	'a[href]:not([aria-disabled="true"]):not([tabindex="-1"])',
	'button:not([disabled]):not([tabindex="-1"])',
	'textarea:not([disabled]):not([tabindex="-1"])',
	'input:not([disabled]):not([tabindex="-1"])',
	'select:not([disabled]):not([tabindex="-1"])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

interface IIsolationMutation {
	element: HTMLElement;
	previousAriaHidden: string | null;
	hadInert: boolean;
}

function isKeyboardFocusable(element: HTMLElement): boolean {
	return element.closest('[inert]') === null && element.getAttribute('aria-disabled') !== 'true';
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isKeyboardFocusable);
}

function isolateModal(target: HTMLElement): () => void {
	const mutations: IIsolationMutation[] = [];
	let current: HTMLElement | null = target;

	while (current && current !== document.body) {
		const parent: HTMLElement | null = current.parentElement;
		if (!parent) {
			break;
		}

		for (const sibling of Array.from(parent.children)) {
			if (sibling === current || !(sibling instanceof HTMLElement)) {
				continue;
			}

			mutations.push({
				element: sibling,
				previousAriaHidden: sibling.getAttribute('aria-hidden'),
				hadInert: sibling.hasAttribute('inert'),
			});
			sibling.setAttribute('aria-hidden', 'true');
			sibling.setAttribute('inert', '');
		}

		current = parent;
	}

	const previousOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';

	return () => {
		mutations.forEach(({ element, previousAriaHidden, hadInert }) => {
			if (previousAriaHidden === null) {
				element.removeAttribute('aria-hidden');
			} else {
				element.setAttribute('aria-hidden', previousAriaHidden);
			}
			if (!hadInert) {
				element.removeAttribute('inert');
			}
		});
		document.body.style.overflow = previousOverflow;
	};
}

interface IUseSideNavModalOptions {
	enabled: boolean;
	onClose?: () => void;
}

/**
 * Treats the open mobile drawer as a modal: traps focus, marks outside content inert, and restores focus on close.
 */
export function useSideNavModal({ enabled, onClose }: IUseSideNavModalOptions): React.RefObject<HTMLDivElement> {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const restoreFocusNodeRef = React.useRef<HTMLElement | null>(null);
	const onCloseRef = React.useRef(onClose);
	const wasEnabledRef = React.useRef(false);

	onCloseRef.current = onClose;

	if (enabled && !wasEnabledRef.current) {
		const activeElement = document.activeElement;
		restoreFocusNodeRef.current = activeElement instanceof HTMLElement ? activeElement : null;
	}
	wasEnabledRef.current = enabled;

	React.useEffect(() => {
		if (!enabled) {
			return;
		}

		const container = containerRef.current;
		if (!container) {
			return;
		}

		const releaseIsolation = isolateModal(container);
		const initialFocusable = getFocusableElements(container)[0];
		initialFocusable?.focus();

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === ESCAPE_KEY) {
				const onClose = onCloseRef.current;
				if (event.defaultPrevented || !onClose) {
					return;
				}
				event.preventDefault();
				onClose();
				return;
			}

			if (event.key !== TAB_KEY) {
				return;
			}

			const elements = getFocusableElements(container);
			if (elements.length === 0) {
				event.preventDefault();
				return;
			}

			const first = elements[0];
			const last = elements[elements.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
				return;
			}
			if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			releaseIsolation();
			const restoreNode = restoreFocusNodeRef.current;
			if (restoreNode?.isConnected) {
				restoreNode.focus();
			}
		};
	}, [enabled]);

	return containerRef;
}
